import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useThemeMode, type ThemeName } from '../../../theme';

const themeOptions: { value: ThemeName; label: string; swatch: string }[] = [
  { value: 'dark', label: 'Dark', swatch: '#6366f1' },
  { value: 'light', label: 'Light', swatch: '#4f46e5' },
  { value: 'cloud2-dark', label: 'Cloud2 Dark', swatch: '#ff578c' },
  { value: 'cloud2-light', label: 'Cloud2 Light', swatch: '#e8456f' },
  { value: 'mr-robot', label: 'Mr. Robot', swatch: '#00ff41' },
];

interface ThemePickerModalProps {
  open: boolean;
  onClose: () => void;
}

const ThemePickerModal: React.FC<ThemePickerModalProps> = ({ open, onClose }) => {
  const { themeName, setThemeName } = useThemeMode();
  const [selected, setSelected] = React.useState<ThemeName>(themeName);

  React.useEffect(() => {
    if (open) setSelected(themeName);
  }, [open, themeName]);

  const handleSave = () => {
    setThemeName(selected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Theme
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {themeOptions.map((opt) => (
            <Box
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 1,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selected === opt.value ? 'primary.main' : 'divider',
                bgcolor: selected === opt.value ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: opt.swatch, flexShrink: 0, border: '2px solid', borderColor: 'divider' }} />
              <Typography variant="body2" sx={{ fontWeight: selected === opt.value ? 600 : 400 }}>
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export { themeOptions };
export default memo(ThemePickerModal);
